import React, { useState, useEffect, useCallback } from "react";
import "../assets/NewFiles/Css/ProjectsDashboard.css";
import { useNavigate } from "react-router-dom";
import api from "../axios/api";
import {
  FaPenSquare,
  FaCheck,
  FaImage,
  FaUserAlt,
  FaMap,
  FaPlus,
} from "react-icons/fa";
import { Tooltip } from "react-tooltip";
import "react-tooltip/dist/react-tooltip.css";
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Pagination,
  Switch,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  useMediaQuery,
  Card,
  CardContent,
  Typography,
  Chip,
  IconButton,
  Grid,
  TextField,
  useTheme,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormHelperText,
} from "@mui/material";
import Loader from "../components/common/Loader";
import Button from "../components/common/styled components/Button";
import CloseIcon from "@mui/icons-material/Close";
import { FaPen, FaTrash } from "react-icons/fa";
import { toast, ToastContainer } from "react-toastify";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";

const SpeciesMaster = () => {
  const [filteredProjects, setFilteredProjects] = useState([]);
  const [page, setPage] = useState(1);
  const rowsPerPage = 10;
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [openModal, setOpenModal] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editingSpecies, setEditingSpecies] = useState(null);
  const [deleteModal, setDeleteModal] = useState(false);
  const [isDelete, setIsDelete] = useState(false);
  const [deleteId, setDeleteId] = useState(null);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  // Updated Formik validation schema
  const validationSchema = Yup.object().shape({
    label: Yup.string()
      .required("Species label is required * ")
      .min(2, "Label must be at least 2 characters")
      .max(100, "Label must not exceed 100 characters")
      .matches(/^[A-Za-z0-9\s]+$/, {
        message: "Label can only contain letters, numbers and spaces",
        excludeEmptyString: true,
      }),

    common_name: Yup.string()
      .required("Common name is required * ")
      .min(2, "Common name must be at least 2 characters")
      .max(100, "Common name must not exceed 100 characters")
      .matches(
        /^[A-Za-z0-9\s]+$/,
        "Common name can only contain letters, numbers and spaces"
      ),

    characterstrics: Yup.string()
      .required("Characteristics are required * ")
      .min(10, "Characteristics must be at least 10 characters")
      .max(500, "Characteristics must not exceed 500 characters")
      .matches(/^[A-Za-z0-9\s,.!'"-]+$/, "Characteristics contains invalid characters"),

    // form_factor: Yup.number()
    //   .required("Form factor is required *")
    //   .positive("Form factor must be a positive number")
    //   .min(0.01, "Form factor must be greater than 0")
    //   .max(
    //     99999999.99,
    //     "Form factor must not exceed 10 digits total with 2 decimals"
    //   )
    //   .test(
    //     "max-digits-after-decimal",
    //     "Form factor must have at most 2 decimal places",
    //     (value) => /^\d+(\.\d{1,2})?$/.test(value?.toString() || "")
    //   )
    //   .test(
    //     "max-total-digits",
    //     "Form factor must have max 10 total digits including decimals",
    //     (value) => {
    //       if (!value) return true;
    //       const [intPart, decimalPart] = value.toString().split(".");
    //       const totalDigits = intPart.length + (decimalPart?.length || 0);
    //       return totalDigits <= 10;
    //     }
    //   )
    //   .typeError("Form factor must be a valid number"),

    // bd_biomass_density: Yup.number()
    //   .required("Biomass density is required *")
    //   .positive("Biomass density must be a positive number")
    //   .min(0.01, "Biomass density must be greater than 0")
    //   .max(
    //     99999999.99,
    //     "Biomass density must not exceed 10 digits total with 2 decimals"
    //   )
    //   .test(
    //     "max-digits-after-decimal",
    //     "Biomass density must have at most 2 decimal places",
    //     (value) => /^\d+(\.\d{1,2})?$/.test(value?.toString() || "")
    //   )
    //   .test(
    //     "max-total-digits",
    //     "Biomass density must have max 10 total digits including decimals",
    //     (value) => {
    //       if (!value) return true;
    //       const [intPart, decimalPart] = value.toString().split(".");
    //       const totalDigits = intPart.length + (decimalPart?.length || 0);
    //       return totalDigits <= 10;
    //     }
    //   )
    //   .typeError("Biomass density must be a valid number"),

    sequence: Yup.number()
      .required("Sequence is required *")
      .integer("Sequence must be a whole number")
      .min(1, "Sequence must be at least 1")
      .max(9999, "Sequence must not exceed 9999")
      .typeError("Sequence must be a valid number"),
  });

  // Initial form values
  const initialValues = {
    label: "",
    common_name: "",
    characterstrics: "",
    form_factor: "",
    bd_biomass_density: "",
    sequence: "",
    species_image: null,
    existing_image_url: "", // Track existing image URL
  };

  // Fetch species data
  const fetchSpecies = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("token");

      const response = await api.get("/api/admin/getSpecies", {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.data.status) {
        setData(response.data.data);
        setFilteredProjects(response.data.data);
      } else {
        console.error("Failed to load species");
        toast.error("Failed to load species data");
      }
    } catch (error) {
      console.error("Error fetching species:", error);
      toast.error("Error fetching species data");
    } finally {
      setLoading(false);
      setIsLoading(false);
    }
  }, []);

  const handlePageChange = (event, value) => {
    setPage(value);
  };

  // Updated form submission handler
  const handleFormSubmit = async (
    values,
    { setSubmitting, resetForm, setValues }
  ) => {
    const token = localStorage.getItem("token");
    try {
      let response;

      // Check if this is a new species and image is missing
      if (!editingSpecies?.id && !values.species_image) {
        toast.error("Image is required for new species");
        return;
      }

      // Create FormData for file upload
      const formData = new FormData();

      // Append all form fields to FormData
      formData.append("id", editingSpecies?.id || null);
      formData.append("label", values.label.trim());
      formData.append("common_name", values.common_name.trim());
      formData.append("characterstrics", values.characterstrics.trim());
      formData.append(
        "form_factor",
        parseFloat(Number(values.form_factor).toFixed(2))
      );
      formData.append(
        "bd_biomass_density",
        parseFloat(Number(values.bd_biomass_density).toFixed(2))
      );
      formData.append(
        "sequence",
        parseFloat(parseFloat(values.sequence).toFixed(1))
      );

      // Handle image upload logic
      if (values.species_image) {
        // New image selected
        formData.append("image", values.species_image);
        formData.append("image_changed", "true");
      } else if (editingSpecies?.id && values.existing_image_url) {
        // Editing and keeping existing image
        formData.append("image_changed", "false");
        formData.append("existing_image_url", values.existing_image_url);
      }

      if (editingSpecies?.id) {
        response = await api.put(
          `/api/admin/updateSpecies/${editingSpecies.id}`,
          formData,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              // Don't set Content-Type header - let the browser set it automatically with boundary
            },
          }
        );
        toast.success("Species updated successfully!");
      } else {
        response = await api.post("/api/admin/createSpecies", formData, {
          headers: {
            Authorization: `Bearer ${token}`,
            // Don't set Content-Type header - let the browser set it automatically with boundary
          },
        });
        toast.success("Species added successfully!");
      }

      if (response.data.status) {
        await fetchSpecies();
        handleCloseModal();
        resetForm();
      } else {
        toast.error(response.data.message || "Operation failed");
      }
    } catch (error) {
      console.error("Form submission failed:", error.response?.data || error);

      if (error.response?.status === 400 && error.response?.data?.errors) {
        const backendErrors = error.response.data.errors;
        toast.error(`Validation Error: ${backendErrors.join(", ")}`);
      } else if (error.response?.data?.message) {
        toast.error(error.response.data.message);
      } else {
        toast.error("Error occurred while saving species");
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleEdit = (e, speciesData) => {
    // Prepare editing data with existing image URL
    const editData = {
      ...speciesData,
      existing_image_url: speciesData.icon_url || "",
      species_image: null, // Reset file input
    };
    setEditingSpecies(editData);
    setOpenModal(true);
  };

  const handleAdd = () => {
    setEditingSpecies(null);
    setOpenModal(true);
  };

  // Delete species data
  const handleDelete = async () => {
    setIsDelete(true);
    const token = localStorage.getItem("token");
    try {
      const response = await api.delete(
        `/api/admin/deleteSpecies/${deleteId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      if (response.data.status) {
        toast.success("Species deleted successfully!");
        await fetchSpecies();
      } else {
        toast.error("Failed to delete species.");
      }
    } catch (error) {
      console.error("Delete failed:", error.response?.data || error);
      toast.error("Error occurred while deleting species.");
    } finally {
      setIsDelete(false);
      setDeleteModal(false);
      setDeleteId(null);
    }
  };

  // Change Species Status
  const handleStatusChange = async (id, newIsActive) => {
    const token = localStorage.getItem("token");

    try {
      const response = await api.put(
        `/api/admin/updateSpeciesStatus/${id}`,
        { isActive: newIsActive },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.data.status) {
        toast.success(`Species ${newIsActive ? "Activated" : "Deactivated"}`);
        await fetchSpecies();
      } else {
        toast.error("Failed to update status.");
      }
    } catch (error) {
      console.error("Status update failed:", error.response?.data || error);
      toast.error("Error occurred while updating status.");
    }
  };

  // Calculate pagination
  const startIndex = (page - 1) * rowsPerPage;
  const endIndex = startIndex + rowsPerPage;
  const paginatedProjects = data.slice(startIndex, endIndex);
  const pageCount = Math.ceil(data.length / rowsPerPage);

  useEffect(() => {
    fetchSpecies();
  }, [fetchSpecies]);

  // Fixed handleCloseModal function
  const handleCloseModal = useCallback(() => {
    setOpenModal(false);
    setEditingSpecies(null);
  }, []);

  // Fixed handleDeleteModalClose function
  const handleDeleteModalClose = useCallback(() => {
    setDeleteModal(false);
    setDeleteId(null);
  }, []);

  // Loading overlay component
  const LoadingOverlay = () => (
    <div
      className="loading-overlay"
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: "rgba(255, 255, 255, 0.7)",
        zIndex: 1000,
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
      }}
    >
      <Loader />
    </div>
  );

  // Custom TextField component with Formik integration
  const FormikTextField = ({
    name,
    label,
    placeholder,
    type = "text",
    multiline = false,
    rows = 1,
    ...props
  }) => (
    <Field name={name}>
      {({ field, meta }) => (
        <TextField
          {...field}
          label={label}
          placeholder={placeholder}
          type={type}
          multiline={multiline}
          rows={multiline ? rows : 1}
          error={meta.touched && Boolean(meta.error)}
          helperText={meta.touched && meta.error}
          variant="outlined"
          size="small"
          fullWidth
          sx={{
            background: "#f9f9f9",
            borderRadius: 2,
            width: "100%",
          }}
          InputProps={{
            sx: {
              height: multiline ? "auto" : 40,
            },
            inputProps: type === "number" ? { min: 0, step: "any" } : {},
          }}
          {...props}
        />
      )}
    </Field>
  );

  return (
    <div>
      <div
        className="projects-dashboard"
        style={{ padding: isMobile ? "1rem" : "2rem" }}
      >
        {isLoading && <LoadingOverlay />}

        <ToastContainer position="top-right" autoClose={3000} />

        <div
          className={isLoading ? "content-disabled" : ""}
          style={{
            pointerEvents: isLoading ? "none" : "auto",
            opacity: isLoading ? 0.6 : 1,
            transition: "opacity 0.3s",
          }}
        >
          {/* Header section with create button */}
          <Box
            sx={{
              display: "flex",
              flexDirection: isMobile ? "column" : "row",
              justifyContent: "space-between",
              alignItems: isMobile ? "stretch" : "center",
              mb: 3,
              gap: 2,
            }}
          >
            <Typography variant="h4" gutterBottom>
              Species Master
            </Typography>
            <Button
              onClick={handleAdd}
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: "8px",
                width: isMobile ? "100%" : "auto",
                padding: "10px 16px",
                boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
              }}
            >
              <FaPlus size="14px" />
              Add New Species
            </Button>

            {/* Delete Confirmation Dialog */}
            <Dialog
              open={deleteModal}
              onClose={handleDeleteModalClose}
              maxWidth="sm"
            >
              <DialogTitle>
                Confirm Delete
                <IconButton
                  onClick={handleDeleteModalClose}
                  style={{ position: "absolute", right: 8, top: 8 }}
                >
                  <CloseIcon />
                </IconButton>
              </DialogTitle>
              <DialogContent>
                Are you sure you want to delete this species? This action cannot
                be undone.
              </DialogContent>
              <DialogActions>
                <Button onClick={handleDeleteModalClose} variant="cancel">
                  Cancel
                </Button>
                <Button
                  onClick={handleDelete}
                  disabled={isDelete}
                  variant="contained"
                  color="danger"
                >
                  {isDelete ? "Deleting..." : "Delete"}
                </Button>
              </DialogActions>
            </Dialog>

            {/* Add/Edit Species Dialog with Formik */}
            <Dialog
              open={openModal}
              onClose={handleCloseModal}
              maxWidth="md"
              fullWidth
            >
              <DialogTitle sx={{ pb: 1 }}>
                {editingSpecies ? "Edit Species" : "Add New Species"}
                <IconButton
                  onClick={handleCloseModal}
                  style={{ position: "absolute", right: 8, top: 8 }}
                >
                  <CloseIcon />
                </IconButton>
              </DialogTitle>

              <Formik
                initialValues={editingSpecies || initialValues}
                validationSchema={validationSchema}
                onSubmit={handleFormSubmit}
                enableReinitialize={true}
              >
                {({
                  isSubmitting,
                  errors,
                  touched,
                  resetForm,
                  values,
                  setFieldValue,
                }) => (
                  <Form>
                    <DialogContent sx={{ pt: 1, pb: 2 }}>
                      {/* Outer wrapper with consistent spacing */}
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          gap: 2,
                        }}
                      >
                        <Field name="species_image">
                          {({ field, form }) => {
                            const { errors, touched, setFieldValue, values } =
                              form;
                            const isNewSpecies = !editingSpecies;

                            return (
                              <FormControl fullWidth margin="normal">
                                <InputLabel shrink htmlFor="species_image">
                                  Species Image {isNewSpecies && "*"}
                                </InputLabel>

                                {/* Flex container for input + preview */}
                                <Box
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 2,
                                    flexWrap: "wrap",
                                    marginTop: 1,
                                  }}
                                >
                                  <input
                                    id="species_image"
                                    type="file"
                                    accept="image/*"
                                    style={{
                                      flex: "1 1 300px",
                                      maxWidth: "800px",
                                      padding: "12.5px 14px",
                                      border:
                                        isNewSpecies &&
                                          !values.species_image &&
                                          touched.species_image
                                          ? "1px solid #d32f2f"
                                          : "1px solid #c4c4c4",
                                      borderRadius: 4,
                                      cursor: "pointer",
                                    }}
                                    onChange={(event) => {
                                      const file = event.currentTarget.files[0];
                                      setFieldValue("species_image", file);
                                    }}
                                  />

                                  {/* Preview beside input */}
                                  {values.species_image ? (
                                    <img
                                      src={URL.createObjectURL(
                                        values.species_image
                                      )}
                                      alt="New Preview"
                                      style={{
                                        width: 60,
                                        height: 60,
                                        objectFit: "cover",
                                        borderRadius: "50%",
                                        border: "2px solid #1976d2",
                                      }}
                                    />
                                  ) : values.existing_image_url &&
                                    editingSpecies ? (
                                    <img
                                      src={values.existing_image_url}
                                      alt="img"
                                      style={{
                                        width: 60,
                                        height: 60,
                                        objectFit: "cover",
                                        borderRadius: "50%",
                                        border: "1px solid #ccc",
                                      }}
                                    />
                                  ) : null}
                                </Box>

                                {/* Error message below */}
                                {isNewSpecies &&
                                  !values.species_image &&
                                  touched.species_image && (
                                    <FormHelperText error>
                                      Image is required for new species
                                    </FormHelperText>
                                  )}
                              </FormControl>
                            );
                          }}
                        </Field>
                        
                          <FormikTextField
                            name="label"
                            label="Species Label "
                            placeholder="Enter Species Label"
                            sx={{ flex: 1, minWidth: 200 }}
                          />

                          <FormikTextField
                            name="common_name"
                            label="Common Name "
                            placeholder="Enter Common Name"
                            sx={{ flex: 1, minWidth: 200 }}
                          />


                        <FormikTextField
                          name="characterstrics"
                          label="Characteristics "
                          placeholder="Enter Characteristics"
                          multiline
                          rows={5}
                        />

                        <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
                          <FormikTextField
                            name="form_factor"
                            label="Form Factor "
                            placeholder="0.00"
                            onKeyDown={(e) => {
                              if (["-", "e", "E"].includes(e.key))
                                e.preventDefault();
                            }}
                            type="number"
                            step="0.1"
                            inputMode="decimal"
                            sx={{ flex: 1, minWidth: 200 }}
                          />

                          <FormikTextField
                            name="bd_biomass_density"
                            label="Biomass Density "
                            placeholder="0.00"
                            type="number"
                            step="0.1"
                            inputMode="decimal"
                            onKeyDown={(e) => {
                              if (["-", "e", "E"].includes(e.key))
                                e.preventDefault();
                            }}
                            sx={{ flex: 1, minWidth: 200 }}
                          />

                          <FormikTextField
                            name="sequence"
                            label="Sequence "
                            placeholder="Enter Sequence Number"
                            type="number"
                            onKeyDown={(e) => {
                              if (["-", "e", "E"].includes(e.key))
                                e.preventDefault();
                            }}
                            sx={{ flex: 1, minWidth: 200 }}
                          />
                        </Box>
                      </Box>
                    </DialogContent>

                    <DialogActions sx={{ p: 2, pt: 1 }}>
                      <Button
                        onClick={() => {
                          handleCloseModal();
                          resetForm();
                        }}
                        variant="cancel"
                        disabled={isSubmitting}
                      >
                        Cancel
                      </Button>
                      <Button
                        type="submit"
                        disabled={isSubmitting}
                        variant="contained"
                        color="primary"
                      >
                        {isSubmitting
                          ? editingSpecies
                            ? "Updating..."
                            : "Adding..."
                          : editingSpecies
                            ? "Update Species"
                            : "Add Species"}
                      </Button>
                    </DialogActions>
                  </Form>
                )}
              </Formik>
            </Dialog>
          </Box>

          {/* Data Table */}
          <TableContainer
            component={Paper}
            sx={{
              boxShadow: "0 4px 8px rgba(0,0,0,0.08)",
              borderRadius: "8px",
              overflow: "hidden",
            }}
          >
            <Table>
              <TableHead sx={{ backgroundColor: "#002F43" }}>
                <TableRow>
                  <TableCell style={{ color: "#fff", fontWeight: "bold" }}>
                    Image
                  </TableCell>
                  <TableCell style={{ color: "#fff", fontWeight: "bold" }}>
                    Label
                  </TableCell>
                  <TableCell style={{ color: "#fff", fontWeight: "bold" }}>
                    Common Name
                  </TableCell>
                  <TableCell style={{ color: "#fff", fontWeight: "bold" }}>
                    Form Factor
                  </TableCell>
                  <TableCell style={{ color: "#fff", fontWeight: "bold" }}>
                    Biomass Density
                  </TableCell>
                  <TableCell style={{ color: "#fff", fontWeight: "bold" }}>
                    Sequence
                  </TableCell>
                  <TableCell style={{ color: "#fff", fontWeight: "bold" }}>
                    Status
                  </TableCell>
                  <TableCell style={{ color: "#fff", fontWeight: "bold" }}>
                    Actions
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedProjects.length > 0 ? (
                  paginatedProjects.map((item) => (
                    <TableRow key={item.id} hover>
                      <TableCell>
                        {item.icon_url ? (
                          <Box
                            component="img"
                            src={item.icon_url}
                            alt={item.label || "Species Icon"}
                            sx={{
                              width: 40,
                              height: 40,
                              borderRadius: "4px",
                              objectFit: "cover",
                              boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                            }}
                          />
                        ) : (
                          <Typography variant="body2" color="textSecondary">
                            No Image
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>{item.label}</TableCell>
                      <TableCell>{item.common_name}</TableCell>
                      <TableCell>
                        {Number(item.form_factor).toFixed(1)}
                      </TableCell>
                      <TableCell>
                        {Number(item.bd_biomass_density).toFixed(1)}
                      </TableCell>
                      <TableCell>{item.sequence}</TableCell>
                      <TableCell>
                        <Switch
                          checked={item.isActive === 1}
                          onChange={() =>
                            handleStatusChange(
                              item.id,
                              item.isActive === 1 ? 0 : 1
                            )
                          }
                          size="small"
                          sx={{
                            "& .MuiSwitch-switchBase.Mui-checked": {
                              color: "green",
                              "& + .MuiSwitch-track": {
                                backgroundColor: "green",
                                opacity: 1,
                              },
                            },
                            "& .MuiSwitch-switchBase": {
                              color: "red",
                              "& + .MuiSwitch-track": {
                                backgroundColor: "red",
                                opacity: 1,
                              },
                            },
                          }}
                        />
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: "flex", gap: 1 }}>
                          <IconButton
                            size="small"
                            onClick={(e) => handleEdit(e, item)}
                            sx={{
                              bgcolor: "#f5f5f5",
                              "&:hover": { bgcolor: "#e0f7fa" },
                            }}
                            title="Edit Species"
                          >
                            <FaPen color="#1976d2" />
                          </IconButton>

                          <IconButton
                            size="small"
                            onClick={() => {
                              setDeleteId(item.id);
                              setDeleteModal(true);
                            }}
                            sx={{
                              bgcolor: "#f5f5f5",
                              "&:hover": { bgcolor: "#ffebee" },
                            }}
                            title="Delete Species"
                          >
                            <FaTrash color="#d32f2f" />
                          </IconButton>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                      <Typography variant="body1" color="textSecondary">
                        {loading
                          ? "Loading species data..."
                          : "No species data found"}
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Pagination */}
          {pageCount > 1 && (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                marginTop: 3,
                marginBottom: 2,
              }}
            >
              <Pagination
                count={pageCount}
                page={page}
                onChange={handlePageChange}
                color="primary"
                className="pagination-container"
                size={isMobile ? "small" : "medium"}
                showFirstButton
                showLastButton
              />
            </Box>
          )}
        </div>
      </div>
    </div>
  );
};

export default SpeciesMaster;
